import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { FullProject } from 'entities';

import { IconButton, Column, Row, Card, Collapse } from 'components/Blocks';

import { ChevronRightIcon } from 'components/Icons';

import { ServiceValues } from './ServiceValues';

import { useProjectProfile } from '../context';

import { cn } from 'styles';
import { setCostsWithInitialValues } from './utils';

import { CountryService, PhaseProps } from './types';

interface SingleServiceProps extends Pick<PhaseProps, 'dispatch' | 'disabled'> {
  service: CountryService;
  country: string;
  actualService: CountryService;

  isOpen: boolean;
  toggleOpen: () => void;
}

export function SingleService({
  service,
  country,
  actualService,
  isOpen,
  toggleOpen,
  ...props
}: SingleServiceProps) {
  const { project } = useProjectProfile();

  const { t } = useLang();

  const name = useMemo(() => {
    const task = project.tasks.find(
      ({ serviceConfigId }) => serviceConfigId === service.id,
    ) as FullProject['tasks'][number];

    if (task.name === service.name) return service.name;

    return `${task.name} (${service.name})`;
  }, [project.tasks, service]);

  const missingFilling = actualService.costs.some(({ value }) => !value);

  return (
    <Card flow="column">
      <Row>
        <IconButton className="text-primary" onClick={toggleOpen}>
          <ChevronRightIcon className={cn('transition-transform', isOpen && 'rotate-90')} />
        </IconButton>

        <span className="font-medium">
          {name}
          {missingFilling ? '*' : ''}
        </span>
      </Row>

      <Collapse expanded={isOpen} onlyRenderIfExpanded>
        <Column key={service.id} gap="6">
          <ServiceValues
            service={setCostsWithInitialValues(service)}
            label={t('initial-metrics')}
            {...props}
            helpText={
              t('this-is-the-price-this-service-had-when-the-project-was-created') as string
            }
            disabled
            country={country}
          />

          <ServiceValues
            service={actualService}
            label={t('actual-metrics')}
            country={country}
            {...props}
            helpText={
              t(
                'sometimes-our-execution-metrics-vary-from-the-price-we-will-charge-our-client-here-is-the-place-to-put-what-was-actually-performed',
              ) as string
            }
          />

          <ServiceValues
            {...props}
            service={service}
            country={country}
            label={t('value-to-be-invoiced')}
            disabled
            helpText={
              t(
                'this-is-the-price-our-client-will-be-charged-if-you-need-to-adjust-it-please-follow-the-reprice-flow-before-finishing-this-project',
              ) as string
            }
          />
        </Column>
      </Collapse>
    </Card>
  );
}
